import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { config } from './config/config';

// Import components (will be created next)
import Header from './components/Header';
import Footer from './components/Footer';
import WelcomeScreen from './components/WelcomeScreen';
import PhotoCapture from './components/PhotoCapture';
import UserInfoForm from './components/UserInfoForm';
import AIAnalysis from './components/AIAnalysis';
import ConditionPage from './components/ConditionPage';
import DocsAnalysis from './components/DocsAnalysis';
import DocsApp from './components/DocsApp';
import PDFGenerator from './components/PDFGenerator';
import DoctorFinder from './components/DoctorFinder';
import WhatsAppBooking from './components/WhatsAppBooking';
import AdminDashboard from './components/AdminDashboard';

import UnifiedDoctorPage from './components/doctors/UnifiedDoctorPage';
import { getDoctorConfig, getAllDoctorSlugs } from './config/doctorConfigs';
import PricingPage from './components/PricingPage';

// Import doctor admin dashboard
import DoctorAdminDashboard from './components/DoctorAdminDashboard';

// Import API documentation
import ApiDocs from './components/ApiDocs';

// Import developer portal and playground
import DeveloperPortal from './components/developers/DeveloperPortal';
import ApiPlayground from './components/playground/ApiPlayground';

// Import utilities
import { getBestAvailableLocation } from './utils/locationServices';

function App() {
  // Application state
  const [appState, setAppState] = useState({
    currentStep: 0,
    patientData: null,
    capturedImage: null,
    analysisResult: null,
    selectedDoctor: null,
    userLocation: null,
    prefetchedDoctors: null,
    isLoading: false,
    error: null,
    analysisMode: null // 'quick' | 'detailed' | null
  });

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.step !== undefined) {
        setAppState(prev => ({ ...prev, currentStep: event.state.step, error: null }));
      } else {
        // Handle direct URL access or refresh
        const path = window.location.pathname;
        const stepMap = {
          '/consultation': 0,
          '/photo-capture': 1,
          '/patient-info': 2,
          '/conditions': 3,
          '/analysis': 4,
          '/report': 5,
          '/find-doctors': 6,
          '/booking': 7
        };
        const step = stepMap[path];
        if (step !== undefined) {
          setAppState(prev => ({ ...prev, currentStep: step, error: null }));
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Application steps configuration
  const steps = [
    { id: 'welcome', title: 'Welcome', component: WelcomeScreen },
    { id: 'photo', title: 'Photo Capture', component: PhotoCapture },
    { id: 'userinfo', title: 'Patient Details', component: UserInfoForm },
    { id: 'conditions', title: 'Skin Concerns', component: ConditionPage },
    { id: 'analysis', title: 'AI Analysis', component: AIAnalysis },
    { id: 'pdf', title: 'Report Generation', component: PDFGenerator },
    { id: 'doctors', title: 'Find Doctor', component: DoctorFinder },
    { id: 'booking', title: 'Book Appointment', component: WhatsAppBooking }
  ];

  // Do NOT request location on load. We request on Analyze step after consent.

  // Navigation functions
  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setAppState(prev => ({ ...prev, currentStep: stepIndex, error: null }));

      // Update URL for direct navigation
      const routes = {
        0: '/consultation',
        1: '/photo-capture',
        2: '/patient-info',
        3: '/conditions',
        4: '/analysis',
        5: '/report',
        6: '/find-doctors',
        7: '/booking'
      };

      if (routes[stepIndex]) {
        window.history.pushState({ step: stepIndex }, '', routes[stepIndex]);
      }
    }
  };

  const nextStep = () => {
    if (appState.currentStep < steps.length - 1) {
      let next = appState.currentStep + 1;

      // Skip ConditionPage (step 3) if in quick mode
      if (next === 3 && appState.analysisMode === 'quick') {
        next = 4; // Jump to AIAnalysis
      }

      // Skip PDFGenerator/report page (step 5) - go directly from analysis to doctors
      if (next === 5) {
        next = 6; // Jump to DoctorFinder
      }

      setAppState(prev => ({ ...prev, currentStep: next, error: null }));

      // Update URL based on step
      const routes = {
        0: '/consultation',
        1: '/photo-capture',
        2: '/patient-info',
        3: '/conditions',
        4: '/analysis',
        5: '/report',
        6: '/find-doctors',
        7: '/booking'
      };

      if (routes[next]) {
        window.history.pushState({ step: next }, '', routes[next]);
      }
    }
  };

  const previousStep = () => {
    if (appState.currentStep > 0) {
      let prev = appState.currentStep - 1;

      // Skip ConditionPage (step 3) if in quick mode when going back from Analysis
      if (prev === 3 && appState.analysisMode === 'quick') {
        prev = 2; // Jump back to UserInfoForm
      }

      // Skip PDFGenerator/report page (step 5) - go directly from doctors back to analysis
      if (prev === 5) {
        prev = 4; // Jump back to AIAnalysis
      }

      setAppState(prevState => ({ ...prevState, currentStep: prev, error: null }));

      // Update URL based on step
      const routes = {
        0: '/consultation',
        1: '/photo-capture',
        2: '/patient-info',
        3: '/conditions',
        4: '/analysis',
        5: '/report',
        6: '/find-doctors',
        7: '/booking'
      };

      if (routes[prev]) {
        window.history.pushState({ step: prev }, '', routes[prev]);
      }
    }
  };

  const resetApp = () => {
    setAppState({
      currentStep: 0,
      patientData: null,
      capturedImage: null,
      analysisResult: null,
      selectedDoctor: null,
      userLocation: appState.userLocation, // Keep location if user granted during session
      prefetchedDoctors: null,
      isLoading: false,
      error: null,
      analysisMode: null
    });
  };

  // Data update functions
  const updatePatientData = (data) => {
    setAppState(prev => ({ ...prev, patientData: { ...prev.patientData, ...data } }));
  };

  const updateCapturedImage = (imageData) => {
    setAppState(prev => ({ ...prev, capturedImage: imageData }));
  };

  const updateAnalysisResult = (result) => {
    setAppState(prev => ({ ...prev, analysisResult: result }));
  };

  const updateSelectedDoctor = (doctor) => {
    setAppState(prev => ({ ...prev, selectedDoctor: doctor }));
  };

  const setLoading = (loading) => {
    setAppState(prev => ({ ...prev, isLoading: loading }));
  };

  const setError = (error) => {
    setAppState(prev => ({ ...prev, error, isLoading: false }));
  };

  const updateAnalysisMode = (mode) => {
    setAppState(prev => ({ ...prev, analysisMode: mode }));
  };

  // Removed prefetch functionality - doctors are fetched directly when needed

  // Update userLocation with a consistent shape { lat, lng }
  const updateUserLocation = (coords) => {
    if (!coords) return;
    const next = coords.lat != null && coords.lng != null
      ? { lat: coords.lat, lng: coords.lng, accuracy: coords.accuracy, source: coords.source || 'gps' }
      : (coords.latitude != null && coords.longitude != null
          ? { lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy, source: coords.source || 'gps' }
          : null);
    if (next) {
      setAppState(prev => ({ ...prev, userLocation: next }));
    }
  };

  // Step progress calculation
  const getProgress = () => {
    return ((appState.currentStep + 1) / steps.length) * 100;
  };

  // Check if step is accessible
  const isStepAccessible = (stepIndex) => {
    switch (stepIndex) {
      case 0: return true; // Welcome always accessible
      case 1: return true; // Photo capture always accessible after welcome
      case 2: return appState.capturedImage !== null; // User info needs image
      case 3:
        // ConditionPage not accessible in quick mode
        if (appState.analysisMode === 'quick') return false;
        return appState.patientData !== null && appState.capturedImage !== null; // Conditions needs patient data and image
      case 4: return appState.patientData !== null && appState.capturedImage !== null; // Analysis needs both
      case 5: return appState.analysisResult !== null; // PDF needs analysis
      case 6: return appState.analysisResult !== null; // Doctor finder needs analysis
      case 7: return appState.selectedDoctor !== null; // Booking needs selected doctor
      default: return false;
    }
  };

  // Current step component
  const CurrentStepComponent = steps[appState.currentStep]?.component;


  return (
    <Router>
      <Routes>
        {/* Dynamic doctor routes */}
        {getAllDoctorSlugs().map(slug => {
          const doctorConfig = getDoctorConfig(slug);
          return doctorConfig ? <Route key={slug} path={`/${slug}`} element={<UnifiedDoctorPage config={doctorConfig} />} /> : null;
        })}

        {/* Dynamic doctor admin dashboards - Clean, no header/footer */}
        {getAllDoctorSlugs().map(slug => {
          const doctorConfig = getDoctorConfig(slug);
          return doctorConfig ? (
            <Route
              key={`${slug}-admin`}
              path={`/${slug}/admin`}
              element={<DoctorAdminDashboard doctorSlug={doctorConfig.slug} />}
            />
          ) : null;
        })}

        {/* Pricing page */}
        <Route path="/pricing" element={<PricingPage />} />

        {/* Main admin - Clean, no header/footer */}
        <Route path="/admin" element={<AdminDashboard />} />

        {/* API Documentation for developers */}
        <Route path="/api-docs" element={<ApiDocs />} />

        {/* Developer Portal */}
        <Route path="/developers/*" element={<DeveloperPortal />} />

        {/* API Playground */}
        <Route path="/playground" element={<ApiPlayground />} />

        {/* Docs-specific routes with DocsAnalysis instead of regular AIAnalysis */}
        <Route
          path="/docs"
          element={
            <div className="min-h-screen bg-gray-50">
              <main className="min-h-screen">
                <WelcomeScreen
                  appState={appState}
                  nextStep={() => window.location.href = '/docs/photo-capture'}
                  updateAnalysisMode={updateAnalysisMode}
                />
              </main>
            </div>
          }
        />

        <Route
          path="/docs/photo-capture"
          element={
            <div className="min-h-screen bg-gray-50">
              <main className="min-h-screen">
                <PhotoCapture
                  appState={appState}
                  nextStep={() => window.location.href = '/docs/patient-info'}
                  updateCapturedImage={updateCapturedImage}
                  setError={setError}
                />
              </main>
            </div>
          }
        />

        <Route
          path="/docs/patient-info"
          element={
            <div className="min-h-screen bg-gray-50">
              <main className="min-h-screen">
                <UserInfoForm
                  appState={appState}
                  nextStep={() => window.location.href = '/docs/analysis'}
                  previousStep={() => window.location.href = '/docs/photo-capture'}
                  updatePatientData={updatePatientData}
                  setError={setError}
                />
              </main>
            </div>
          }
        />

        <Route
          path="/docs/analysis"
          element={
            <div className="min-h-screen bg-gray-50">
              <main className="min-h-screen">
                <DocsAnalysis
                  appState={appState}
                  nextStep={() => {}} // No next step for docs
                  previousStep={() => window.location.href = '/docs/patient-info'}
                  goToStep={(step) => {
                    const routes = ['/docs', '/docs/photo-capture', '/docs/patient-info', '/docs/analysis'];
                    if (routes[step]) window.location.href = routes[step];
                  }}
                  updateAnalysisResult={updateAnalysisResult}
                  setLoading={setLoading}
                  setError={setError}
                />
              </main>
            </div>
          }
        />

        {/* Main app routes with header/footer */}
        <Route
          path="/"
          element={
            <div className="min-h-screen bg-gray-50">
              <Header hideHeader={appState.currentStep >= 1} />
              <main className="min-h-screen">
                {CurrentStepComponent && (
                  <CurrentStepComponent
                    appState={appState}
                    nextStep={nextStep}
                    previousStep={previousStep}
                    goToStep={goToStep}
                    resetApp={resetApp}
                    updatePatientData={updatePatientData}
                    updateCapturedImage={updateCapturedImage}
                    updateAnalysisResult={updateAnalysisResult}
                    updateSelectedDoctor={updateSelectedDoctor}
                    setLoading={setLoading}
                    setError={setError}
                    updateUserLocation={updateUserLocation}
                    isStepAccessible={isStepAccessible}
                    updateAnalysisMode={updateAnalysisMode}
                  />
                )}
              </main>
              <Footer hideFooter={appState.currentStep >= 1} />
            </div>
          }
        />

        <Route
          path="/consultation"
          element={
            <div className="min-h-screen bg-gray-50">
              <Header hideHeader={appState.currentStep >= 1} />
              <main className="min-h-screen">
                {CurrentStepComponent && (
                  <CurrentStepComponent
                    appState={appState}
                    nextStep={nextStep}
                    previousStep={previousStep}
                    goToStep={goToStep}
                    resetApp={resetApp}
                    updatePatientData={updatePatientData}
                    updateCapturedImage={updateCapturedImage}
                    updateAnalysisResult={updateAnalysisResult}
                    updateSelectedDoctor={updateSelectedDoctor}
                    setLoading={setLoading}
                    setError={setError}
                    updateUserLocation={updateUserLocation}
                    isStepAccessible={isStepAccessible}
                    updateAnalysisMode={updateAnalysisMode}
                  />
                )}
              </main>
              <Footer hideFooter={appState.currentStep >= 1} />
            </div>
          }
        />

        {/* Catch all - redirect to main app */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
